import Menu, { MenuProps } from '@mui/material/Menu';
import { MenuItemProps } from '@mui/material/MenuItem';
import { SxProps, Theme } from '@mui/material/styles';
import {
   ElementType,
   forwardRef,
   HTMLAttributes,
   ReactNode,
   RefObject,
   useImperativeHandle,
   useRef,
} from 'react';

import { IconMenuItem } from './IconMenuItem';
import Box from '@mui/material/Box';
import useStore from 'reusable/mobx-store-utils';
import NestedMenuItemStore from './NestedMenuItemStore';
import { observer } from 'mobx-react-lite';

export type NestedMenuItemProps = Omit<MenuItemProps, 'button'> & {
   parentMenuOpen: boolean
   component?: ElementType
   label?: string
   icon?: ReactNode
   rightIcon?: ReactNode
   children?: ReactNode
   className?: string
   tabIndex?: number
   disabled?: boolean
   ContainerProps?: HTMLAttributes<HTMLElement> & { ref: RefObject<HTMLElement | null> }
   MenuProps?: Partial<Omit<MenuProps, 'children'>>
   button?: true | undefined
   /** Overrides the outer container's default `{ px: '10px', py: '5px' }` spacing. */
   containerSx?: SxProps<Theme>
};

const NestedMenuItem = observer(forwardRef<HTMLLIElement | null, NestedMenuItemProps>(function NestedMenuItem(
   props,
   ref
) {
   const {
      parentMenuOpen,
      rightIcon,
      label,
      icon = null,
      children,
      className,
      tabIndex: tabIndexProp,
      MenuProps,
      containerSx,
      ...MenuItemProps
   } = props

   const { ref: containerRefProp, ...ContainerProps } = props.ContainerProps || {}

   const menuItemRef = useRef<HTMLLIElement | null>(null)
   useImperativeHandle(ref, () => menuItemRef.current!)

   const containerRef = useRef<HTMLDivElement | null>(null)
   useImperativeHandle(containerRefProp, () => containerRef.current)

   const menuContainerRef = useRef<HTMLDivElement | null>(null)

   const nestedMenuItemStore = useStore(sp => new NestedMenuItemStore(sp), { ContainerProps, containerRef, menuContainerRef, parentMenuOpen })

   // Root element must have a `tabIndex` attribute for keyboard navigation
   let tabIndex
   if (!props.disabled) {
      tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1
   }

   return (
      <Box
         {...ContainerProps}
         ref={containerRef}
         onFocus={nestedMenuItemStore.handleFocus}
         tabIndex={tabIndex}
         onMouseEnter={nestedMenuItemStore.handleMouseEnter}
         onMouseLeave={nestedMenuItemStore.handleMouseLeave}
         onKeyDown={nestedMenuItemStore.handleKeyDown}
         sx={{ px: '10px', py: '5px', ...containerSx }}
      >
         <IconMenuItem
            MenuItemProps={MenuItemProps}
            className={className}
            ref={menuItemRef}
            icon={icon}
            rightIcon={rightIcon}
            label={label}
         />

         <Menu
            anchorEl={menuItemRef.current}
            anchorOrigin={{
               horizontal: 'right',
               vertical: 'top',
            }}
            transformOrigin={{
               horizontal: 'left',
               vertical: 'top',
            }}
            open={nestedMenuItemStore.open}
            autoFocus={false}
            disableAutoFocus
            disableEnforceFocus
            onClose={nestedMenuItemStore.closeSubMenu}
            sx={{ mt: '-18px', ml: '11px', maxHeight: '650px', '& .MuiPaper-root': { borderRadius: '8px', width: '310px' }, '& .MuiMenuItem-root': { borderRadius: '0px', opacity: '1 !important' }, pointerEvents: 'none' }}
            {...MenuProps}
            PopoverClasses={{ root: 'MuiPopover--backdrop-fix' }}
         >
            <Box ref={menuContainerRef} style={{ pointerEvents: 'auto' }}>
               {children}
            </Box>
         </Menu>
      </Box>
   )
}))

NestedMenuItem.displayName = 'NestedMenuItem'
export { NestedMenuItem }